import { HelmetProvider } from '@dr.pogodin/react-helmet';
import React from 'react';
import { CookiesProvider, useCookies } from 'react-cookie';
import ReactGA from 'react-ga4';
import { BrowserRouter, useLocation } from 'react-router';
import './App.css';
import AppRoot from './common/AppRoot';
import Config, { type MultiLang } from './config';

const isAnalyticsEnabled = import.meta.env.PROD && Config.GOOGLE_ANALYTICS_TRACKING_ID !== '';

if (isAnalyticsEnabled) {
    // Page views are sent from the location effect in AppMain, the first one included;
    // gtag's own page_view on config would count the landing page twice.
    ReactGA.initialize(Config.GOOGLE_ANALYTICS_TRACKING_ID, { gtagOptions: { send_page_view: false } });
}

const mlang = ['en', 'ja'] as const;
const isMultiLang = (name: string): name is MultiLang => {
    return mlang.some((value) => value === name);
};

const AppMain: React.FC = () => {
    const [cookies, setCookie] = useCookies();
    const typedCookies = cookies as Record<string, string>;
    const [lang, setLang] = React.useState<MultiLang>(
        isMultiLang(typedCookies?.ml_lang ?? '') ? (cookies.ml_lang as MultiLang) : 'en'
    );
    const location = useLocation();

    React.useEffect(() => {
        const params = new URLSearchParams(location.search);
        const ml_lang = params.get('ml_lang');
        if (ml_lang != null && ['en', 'ja'].includes(ml_lang)) {
            if (cookies.ml_lang !== ml_lang) {
                setCookie('ml_lang', ml_lang);
            }
            if (lang !== ml_lang) {
                setLang(ml_lang as MultiLang);
            }
        }
        window.scrollTo(0, 0);
    }, [location, cookies, setCookie, lang]);

    React.useEffect(() => {
        if (isAnalyticsEnabled) {
            ReactGA.send({ hitType: 'pageview', page: location.pathname + location.search });
        }
    }, [location.pathname, location.search]);

    return <AppRoot lang={lang} />;
};

const App: React.FC = () => {
    return (
        <BrowserRouter>
            <CookiesProvider>
                <HelmetProvider>
                    <AppMain />
                </HelmetProvider>
            </CookiesProvider>
        </BrowserRouter>
    );
};

export default App;
